import { motion } from "framer-motion";
import { useEffect, useRef, type JSX } from "react";

import { fadeRise, messageIn } from "../lib/motion";
import { type Message } from "../state/VossProvider";
import { useVoss } from "../state/VossProvider";
import { type UiOrigin, type UiTurn } from "../lib/types";
import { failureCopy } from "../lib/failures";
import { Badge } from "./primitives";
import { MissionCard } from "./MissionCard";
import { ProposalReviewCard } from "./ProposalReviewCard";
import { IconLock, IconShield, IconSparkle } from "./icons";

const SUGGESTIONS = ["Summarise what you know about my work", "What's on my radar this week?", "Draft a calm note to my team", "Help me plan the launch"];

const CONFIDENCE_LABEL: Record<string, string> = { high: "High", medium: "Medium", low: "Low", none: "" };

// Honest trust taxonomy: "Evidence found" (with confidence) ONLY when substantive claims are
// supported — never a bare "Verified". Weak overlap is "Related memory · Low confidence", never
// presented as fact. Unsupported / not-found is shown as such.
function VerificationBadge({ turn }: { turn: UiTurn }): JSX.Element | null {
  if (turn.outcome !== "answered") return <Badge tone="warn">{turn.outcome === "declined" ? "Declined" : "Failed"}</Badge>;
  const { verdict, confidence } = turn.verification;
  if (verdict === "evidence-backed")
    return (
      <Badge tone="good" dot>
        <IconShield /> Evidence found · {CONFIDENCE_LABEL[confidence]} confidence
      </Badge>
    );
  if (verdict === "related") return <Badge tone="warn">Related memory · Low confidence</Badge>;
  if (verdict === "unverified") return <Badge tone="warn">Not found in memory — not presented as fact</Badge>;
  return <Badge tone="neutral">Model answer</Badge>;
}

function ExecutionIndicator({ turn }: { turn: UiTurn }): JSX.Element {
  return turn.executedLocally ? (
    <Badge tone="good">
      <IconLock /> Local
    </Badge>
  ) : (
    <Badge tone="neutral" dot>
      Cloud
    </Badge>
  );
}

// Human-readable origin labels — the ONLY strings the UI may name an origin with. A closed map over
// UiOrigin (deferred origins are not in the type, so they can never be named). Deliberately plain: an
// origin says WHERE an answer came from, never that it is true — no "Verified", "Guaranteed", "Fact
// Checked", "Truth", "Certain", "100%".
const ORIGIN_LABEL: Record<UiOrigin, string> = {
  "memory.personal": "From your memory",
  "memory.workspace": "From this workspace",
  "memory.enterprise": "From your organization",
  model: "Model answer — not drawn from your stored evidence",
  "insufficient-evidence": "Insufficient evidence to ground this answer",
};

// Human-readable proof, derived only from the manifest origin + the turn's supporting count. Never a
// detector name, memory id, or raw proof string — those never cross the wire.
function originProof(origin: UiOrigin, supportingCount: number): string {
  switch (origin) {
    case "memory.personal":
    case "memory.workspace":
    case "memory.enterprise":
      return `Grounded in ${supportingCount} supporting memory item${supportingCount === 1 ? "" : "s"}.`;
    case "model":
      return "Generated by the model from general reasoning, not from your stored evidence.";
    case "insufficient-evidence":
      return "VOSS did not find enough grounding, so it did not assert an answer.";
  }
}

const ORIGIN_CONFIDENCE_LABEL: Record<string, string> = { high: "High", medium: "Medium", low: "Low", none: "None" };

// C4 Origin panel: a collapsed-by-default, keyboard-reachable disclosure showing WHERE a completed
// answer came from. A pure consumer of the frozen OriginManifest — it lists only the origins the
// classifier emitted, shows the manifest's OWN confidence (never recomputed or overridden), and renders
// human proof. Mixed origins carry the manifest's weakest-wins confidence; none is highlighted over
// another (no laundering). Deferred origins are absent from the type, so they can never appear here.
function OriginPanel({ turn }: { turn: UiTurn }): JSX.Element | null {
  if (turn.outcome !== "answered") return null; // origins accompany a completed answer only
  const { origins, confidence } = turn.origins;
  if (origins.length === 0) return null;
  const { supportingCount } = turn.verification;
  return (
    <details className="voss-msg__origin">
      <summary aria-label="Where this answer came from">Where this came from</summary>
      <div className="voss-msg__origin-body">
        <ul className="voss-msg__origin-list">
          {origins.map((o) => (
            <li key={o} className="voss-msg__origin-item">
              <span className="voss-msg__origin-label">{ORIGIN_LABEL[o]}</span>
              <span className="voss-msg__origin-proof">{originProof(o, supportingCount)}</span>
            </li>
          ))}
        </ul>
        <p className="voss-msg__origin-confidence">Confidence: {ORIGIN_CONFIDENCE_LABEL[confidence]}</p>
      </div>
    </details>
  );
}

function AssistantMeta({ turn }: { turn: UiTurn }): JSX.Element {
  const { supportingCount, unverifiedClaims } = turn.verification;
  const unverified = turn.outcome === "answered" ? unverifiedClaims : [];
  return (
    <>
      <motion.div className="voss-msg__meta" variants={fadeRise} initial="hidden" animate="visible">
        <VerificationBadge turn={turn} />
        {turn.outcome === "answered" ? <ExecutionIndicator turn={turn} /> : null}
        {supportingCount > 0 ? (
          <Badge tone="accent">
            <IconSparkle width={12} height={12} /> Supporting memories: {supportingCount}
          </Badge>
        ) : null}
        {unverified.length > 0 ? <Badge tone="warn">{unverified.length} claim{unverified.length === 1 ? "" : "s"} not verified</Badge> : null}
      </motion.div>
      {unverified.length > 0 ? (
        <details className="voss-msg__unverified">
          <summary>Not verified against memory ({unverified.length}) — shown, not asserted as fact</summary>
          <ul>
            {unverified.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </details>
      ) : null}
      <OriginPanel turn={turn} />
    </>
  );
}

/** Typed, provider-abstracted failure notice for a turn that did not complete. Never a fabricated answer. */
function FailureNotice({ failure }: { failure: NonNullable<Message["failure"]> }): JSX.Element {
  const { retry, newChat, sending } = useVoss();
  const copy = failureCopy(failure.code);
  return (
    <div className="voss-msg__failure" role="alert">
      {failure.partial ? <span className="voss-msg__failure-note">This response was interrupted before it finished.</span> : null}
      <span className="voss-msg__failure-title">{copy.title}</span>
      {copy.action === "retry" && failure.retryable ? (
        <button type="button" className="voss-msg__failure-action" onClick={() => void retry()} disabled={sending}>
          Retry
        </button>
      ) : null}
      {copy.action === "new-chat" ? (
        <button type="button" className="voss-msg__failure-action" onClick={() => newChat()}>
          Start new chat
        </button>
      ) : null}
    </div>
  );
}

function MessageView({ message }: { message: Message }): JSX.Element {
  return (
    <motion.div className={`voss-msg voss-msg--${message.role}`} variants={messageIn} initial="hidden" animate="visible">
      {message.text.length > 0 || !message.failure ? (
        <div className={`voss-msg__bubble${message.failure ? " voss-msg__bubble--failed" : ""}`}>
          {message.text}
          {message.streaming && message.text.length > 0 ? <span className="voss-caret" aria-hidden /> : null}
          {message.streaming && message.text.length === 0 ? (
            <span className="voss-status">
              <span className="voss-status__pulse" /> {message.status ?? "Thinking"}
            </span>
          ) : null}
        </div>
      ) : null}
      {message.role === "assistant" && message.failure ? <FailureNotice failure={message.failure} /> : null}
      {message.role === "assistant" && message.turn ? <AssistantMeta turn={message.turn} /> : null}
      {message.role === "assistant" && message.review && message.proposalId && !message.missionId ? (
        <ProposalReviewCard review={message.review} proposalId={message.proposalId} closed={message.proposalClosed} />
      ) : null}
      {message.role === "assistant" && message.missionId ? <MissionCard missionId={message.missionId} /> : null}
    </motion.div>
  );
}

/** The conversation surface: premium greeting + suggestions when empty, streamed messages otherwise. */
export function Conversation(): JSX.Element {
  const { activeConversation, send, currentWorkspace } = useVoss();
  const bottomRef = useRef<HTMLDivElement>(null);
  const messages = activeConversation?.messages ?? [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: "end", behavior: "smooth" });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="voss-thread">
        <motion.div className="voss-thread__inner voss-thread__empty" variants={fadeRise} initial="hidden" animate="visible">
          <div className="voss-brand__mark voss-brand__mark--empty" aria-hidden />
          <h2 className="voss-empty__title">How can I help in {currentWorkspace?.label ?? "VOSS"}?</h2>
          <p className="voss-empty__sub">Ask anything. VOSS keeps this workspace’s context private and grounds answers in what it knows.</p>
          <div className="voss-suggestions">
            {SUGGESTIONS.map((s, i) => (
              <motion.button
                key={s}
                type="button"
                className="voss-suggestion"
                onClick={() => void send(s)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i + 0.1, duration: 0.24 }}
              >
                {s}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="voss-thread">
      <div className="voss-thread__inner">
        {messages.map((m) => (
          <MessageView key={m.id} message={m} />
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
